import { useEffect, useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

// Navigation context storage keys
const NAVIGATION_KEYS = {
  CURRENT_PAGE: "currentPage",
  LAST_PAGE: "lastPage",
  NAVIGATION_STACK: "navigationStack",
} as const;

// Debug logging for navigation (set to true to enable debugging)
const DEBUG_NAVIGATION = false;

const debugLog = (...args: any[]) => {
  if (DEBUG_NAVIGATION) {
    console.log("[Navigation Debug]", ...args);
  }
};

// Enhanced entity data cache and fetching system
interface EntityData {
  id: string;
  title?: string;
  name?: string;
  code?: string;
  order_number?: number;
  series?: { id: string; title: string; code: string };
}

const entityCache = new Map<string, EntityData>();
const cacheExpiry = new Map<string, number>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Extract entity ID and type from path
const parsePathForEntity = (
  path: string
): { type: string; id: string } | null => {
  const seriesMatch = path.match(/^\/series\/([^\/]+)/);
  if (seriesMatch) return { type: "series", id: seriesMatch[1] };

  const runMatch = path.match(/^\/runs\/([^\/]+)/);
  if (runMatch) return { type: "run", id: runMatch[1] };

  const labMatch = path.match(/^\/labs\/([^\/]+)/);
  if (labMatch) return { type: "lab", id: labMatch[1] };

  const studentMatch = path.match(/^\/students\/([^\/]+)/);
  if (studentMatch) return { type: "student", id: studentMatch[1] };

  const paymentMatch = path.match(/^\/payments\/([^\/]+)/);
  if (paymentMatch) return { type: "payment", id: paymentMatch[1] };

  return null;
};

// Fetch entity data from Supabase
const fetchEntityData = async (
  type: string,
  id: string
): Promise<EntityData | null> => {
  const cacheKey = `${type}:${id}`;

  // Check cache first
  const cached = entityCache.get(cacheKey);
  const expiry = cacheExpiry.get(cacheKey);
  if (cached && expiry && Date.now() < expiry) {
    return cached;
  }

  try {
    let data: EntityData | null = null;

    switch (type) {
      case "series": {
        const { data: series } = await supabase
          .from("series")
          .select("id, title, code")
          .eq("id", id)
          .single();
        if (series) {
          data = { id: series.id, title: series.title, code: series.code };
        }
        break;
      }

      case "run": {
        const { data: run } = await supabase
          .from("runs")
          .select(
            `
            id, 
            name,
            series:series_id (id, title, code)
          `
          )
          .eq("id", id)
          .single();
        if (run) {
          data = {
            id: run.id,
            name: run.name,
            series: run.series as { id: string; title: string; code: string },
          };
        }
        break;
      }

      case "lab": {
        const { data: lab } = await supabase
          .from("labs")
          .select(
            `
            id, 
            title, 
            order_number,
            series:series_id (id, title, code)
          `
          )
          .eq("id", id)
          .single();
        if (lab) {
          data = {
            id: lab.id,
            title: lab.title,
            order_number: lab.order_number,
            series: lab.series as { id: string; title: string; code: string },
          };
        }
        break;
      }

      case "student": {
        const { data: student } = await supabase
          .from("students")
          .select("id, first_name, last_name")
          .eq("id", id)
          .single();
        if (student) {
          data = {
            id: student.id,
            name: `${student.first_name} ${student.last_name}`.trim(),
          };
        }
        break;
      }

      case "payment": {
        const { data: payment } = await supabase
          .from("payments")
          .select(
            `
            id, 
            amount,
            student:student_id (id, first_name, last_name)
          `
          )
          .eq("id", id)
          .single();
        if (payment && payment.student) {
          const student = payment.student as {
            id: string;
            first_name: string;
            last_name: string;
          };
          data = {
            id: payment.id,
            name: `Payment for ${student.first_name} ${student.last_name}`.trim(),
          };
        }
        break;
      }
    }

    if (data) {
      entityCache.set(cacheKey, data);
      cacheExpiry.set(cacheKey, Date.now() + CACHE_DURATION);
    }

    return data;
  } catch (error) {
    debugLog("Error fetching entity data:", error);
    return null;
  }
};

// Generate enhanced navigation label with entity data
const generateEnhancedLabel = async (
  targetPath: string,
  wasVisited: boolean
): Promise<string> => {
  const entity = parsePathForEntity(targetPath);

  if (!entity) {
    // Handle non-entity paths
    if (targetPath === "/series")
      return wasVisited ? "Back to all Series" : "Go to all Series";
    if (targetPath === "/runs")
      return wasVisited ? "Back to all Groups" : "Go to all Groups";
    if (targetPath === "/labs")
      return wasVisited ? "Back to all Labs" : "Go to all Labs";
    if (targetPath === "/students")
      return wasVisited ? "Back to all Students" : "Go to all Students";
    if (targetPath === "/payments")
      return wasVisited ? "Back to all Payments" : "Go to all Payments";
    return wasVisited ? "Back" : "Go to";
  }

  const entityData = await fetchEntityData(entity.type, entity.id);

  if (!entityData) {
    // Fallback to basic labels if data fetch fails
    const entityName =
      entity.type.charAt(0).toUpperCase() + entity.type.slice(1);
    return wasVisited ? `Back to ${entityName}` : `Go to ${entityName}`;
  }

  const prefix = wasVisited ? "Back to" : "Go to";

  switch (entity.type) {
    case "series":
      if (entityData.code !== undefined) {
        return `${prefix} ${entityData.code}`;
      }
      return `${prefix} Series`;

    case "run":
      if (entityData.name && entityData.series) {
        return `${prefix} Group ${entityData.name} of ${entityData.series.code}`;
      }
      return `${prefix} ${entityData.name || "Group"}`;

    case "lab":
      if (entityData.title && entityData.order_number && entityData.series) {
        return `${prefix} Lab ${entityData.order_number}: ${entityData.title}`;
      }
      return `${prefix} ${entityData.title || "Lab"}`;

    case "student":
      return `${prefix} ${entityData.name || "Student"}`;

    case "payment":
      return `${prefix} ${entityData.name || "Payment"}`;

    default:
      return wasVisited ? "Back" : "Go to";
  }
};

export const useNavigationTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Get current navigation stack
    const stackJson = sessionStorage.getItem(NAVIGATION_KEYS.NAVIGATION_STACK);
    const stack: string[] = stackJson ? JSON.parse(stackJson) : [];

    debugLog("Current location:", location.pathname);
    debugLog("Current stack:", stack);

    // Don't track the same page twice in a row
    if (stack[stack.length - 1] !== location.pathname) {
      // Store previous page as last page
      if (stack.length > 0) {
        sessionStorage.setItem(
          NAVIGATION_KEYS.LAST_PAGE,
          stack[stack.length - 1]
        );
      }

      // Check for potential navigation loops (A -> B -> A scenarios)
      // If we're going back to a page that was 2 steps ago, remove the loop
      let newStack = [...stack, location.pathname];

      if (newStack.length >= 3) {
        const currentPage = location.pathname;
        const previousPage = newStack[newStack.length - 2];
        const twoStepsAgo = newStack[newStack.length - 3];

        debugLog("Loop detection:", { currentPage, previousPage, twoStepsAgo });

        // If current page is same as two steps ago, we have a potential loop
        // But don't remove hierarchical navigation patterns (Series -> Run -> Lab -> Run)
        if (currentPage === twoStepsAgo) {
          const isHierarchicalPattern =
            // Series -> Run -> Lab -> Run pattern
            (currentPage.match(/^\/runs\/[^\/]+$/) &&
              previousPage.match(/^\/series\/[^\/]+\/labs\/[^\/]+$/)) ||
            // Run -> Lab -> Run -> Series pattern
            (currentPage.match(/^\/series\/[^\/]+$/) &&
              previousPage.match(/^\/runs\/[^\/]+$/));

          debugLog("Hierarchical pattern detected:", isHierarchicalPattern);

          // Only remove loop if it's not a valid hierarchical pattern
          if (!isHierarchicalPattern) {
            newStack = [...newStack.slice(0, -2), currentPage];
            debugLog("Loop removed, new stack:", newStack);
          } else {
            debugLog("Preserving hierarchical navigation pattern");
          }
        }
      }

      // Limit to last 10 pages to prevent memory issues but keep enough for complex navigation
      newStack = newStack.slice(-10);

      debugLog("Final stack:", newStack);

      sessionStorage.setItem(
        NAVIGATION_KEYS.NAVIGATION_STACK,
        JSON.stringify(newStack)
      );
      sessionStorage.setItem(NAVIGATION_KEYS.CURRENT_PAGE, location.pathname);
    }
  }, [location.pathname]);
};

export const useSmartBack = (fallbackPath: string) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [enhancedLabel, setEnhancedLabel] = useState<string>("Back");

  const getBackDestination = useCallback(() => {
    const currentPath = location.pathname;

    debugLog("Getting back destination for:", currentPath);

    // Get navigation stack to check if destination was visited
    const stackJson = sessionStorage.getItem(NAVIGATION_KEYS.NAVIGATION_STACK);
    const stack: string[] = stackJson ? JSON.parse(stackJson) : [];

    // Helper function to determine if a path was actually visited (not just inferred)
    const wasActuallyVisited = (targetPath: string): boolean => {
      return stack.some((path) => path === targetPath);
    };

    // Get hierarchical parent for current page type (prioritize logical structure over history)

    // Lab detail pages - always go to their series
    if (currentPath.match(/^\/labs\/[^\/]+$/)) {
      // Extract series ID from the lab detail page or fallback path
      if (fallbackPath.includes("/series/")) {
        const wasVisited = wasActuallyVisited(fallbackPath);
        return { path: fallbackPath, label: "Back", wasVisited };
      }
      // If coming from an edit page, ignore it and go to labs list
      const previousPage = stack.length > 1 ? stack[stack.length - 2] : null;
      if (previousPage && previousPage.includes("/edit")) {
        const wasVisited = wasActuallyVisited("/labs");
        return { path: "/labs", label: "Back", wasVisited };
      }
      const wasVisited = wasActuallyVisited("/labs");
      return { path: "/labs", label: "Back", wasVisited };
    }

    // Run detail pages - prioritize logical navigation based on context
    if (currentPath.match(/^\/runs\/[^\/]+$/)) {
      const previousPage = stack.length > 1 ? stack[stack.length - 2] : null;

      debugLog("Run detail navigation - previous page:", previousPage);

      // PRIORITY 1: If we came from a specific page, go back to it (respect actual navigation)
      if (previousPage && previousPage !== currentPath) {
        // If we came from the series list, go back to the series list
        if (previousPage === "/series") {
          debugLog("Came from series list, going back to series list");
          const wasVisited = wasActuallyVisited("/series");
          return { path: "/series", label: "Back", wasVisited };
        }

        // If we came from a lab detail page, prioritize going back to the original source
        // rather than getting stuck in Series detail -> Run -> Lab -> Run loops
        if (previousPage.match(/^\/series\/[^\/]+\/labs\/[^\/]+$/)) {
          debugLog(
            "Detected navigation from lab to run, looking for original navigation source"
          );

          // First priority: Look for the series list page in the stack (where user likely started)
          if (stack.includes("/series")) {
            debugLog("Found series list in stack, going back to series list");
            const wasVisited = wasActuallyVisited("/series");
            return { path: "/series", label: "Back", wasVisited };
          }

          // Second priority: Look for any series detail page in the stack
          const seriesPage = stack.find((page) =>
            page.match(/^\/series\/[^\/]+$/)
          );
          if (seriesPage) {
            debugLog("Found series page in stack:", seriesPage);
            const wasVisited = wasActuallyVisited(seriesPage);
            return { path: seriesPage, label: "Back", wasVisited };
          }

          // Third priority: Extract series ID from the lab URL and navigate to series detail
          const seriesMatch = previousPage.match(/^\/series\/([^\/]+)/);
          if (seriesMatch) {
            debugLog("Extracting series ID from lab URL:", seriesMatch[1]);
            const seriesPath = `/series/${seriesMatch[1]}`;
            const wasVisited = wasActuallyVisited(seriesPath);
            return { path: seriesPath, label: "Back", wasVisited };
          }
        }

        // For any other previous page that was actually visited, go back to it
        if (
          !previousPage.includes("/add") &&
          !previousPage.includes("/new") &&
          !previousPage.includes("/edit")
        ) {
          debugLog("Going back to previous page:", previousPage);
          const wasVisited = true; // Previous page was definitely visited
          return { path: previousPage, label: "Back", wasVisited };
        }
      }

      // Check if we came from an edit page and ignore it
      if (previousPage && previousPage.includes("/edit")) {
        debugLog("Came from edit page, looking for better navigation option");

        // Look for the series list page in the stack
        if (stack.includes("/series")) {
          debugLog("Found series list in stack, going back to series list");
          const wasVisited = wasActuallyVisited("/series");
          return { path: "/series", label: "Back", wasVisited };
        }

        // Look for any series detail page in the stack
        const seriesPage = stack.find((page) =>
          page.match(/^\/series\/[^\/]+$/)
        );
        if (seriesPage) {
          debugLog("Found series page in stack:", seriesPage);
          const wasVisited = wasActuallyVisited(seriesPage);
          return { path: seriesPage, label: "Back", wasVisited };
        }
      }

      // PRIORITY 2: Extract series ID from fallback path or navigate to series (only if no valid previous page)
      if (fallbackPath.includes("/series/")) {
        debugLog("Using fallback path for run:", fallbackPath);
        const wasVisited = wasActuallyVisited(fallbackPath);
        return { path: fallbackPath, label: "Back", wasVisited };
      }

      // PRIORITY 3: Default to runs list
      const wasVisited = wasActuallyVisited("/runs");
      return { path: "/runs", label: "Back", wasVisited };
    }

    // Series detail pages - handle context-aware navigation
    if (currentPath.match(/^\/series\/[^\/]+$/)) {
      const previousPage = stack.length > 1 ? stack[stack.length - 2] : null;

      // If we came from a lab or run detail, go back to series list for better UX
      // This prevents getting stuck in Series -> Run -> Lab -> Series loops
      if (
        previousPage &&
        (previousPage.includes("/labs/") || previousPage.includes("/runs/"))
      ) {
        const wasVisited = wasActuallyVisited("/series");
        return { path: "/series", label: "Back", wasVisited };
      }
      // If coming from an edit page, ignore it and go to series list
      if (previousPage && previousPage.includes("/edit")) {
        const wasVisited = wasActuallyVisited("/series");
        return { path: "/series", label: "Back", wasVisited };
      }
      // If coming from another series page, use history
      if (
        previousPage &&
        previousPage !== currentPath &&
        !previousPage.includes("/labs/") &&
        !previousPage.includes("/runs/")
      ) {
        const wasVisited = true; // Previous page was definitely visited
        return { path: previousPage, label: "Back", wasVisited };
      }
      const wasVisited = wasActuallyVisited("/series");
      return { path: "/series", label: "Back", wasVisited };
    }

    // Student detail pages - go to students list (unless specific context)
    if (currentPath.match(/^\/students\/[^\/]+$/)) {
      const previousPage = stack.length > 1 ? stack[stack.length - 2] : null;

      if (previousPage && previousPage.includes("/runs/")) {
        const wasVisited = true; // Previous page was definitely visited
        return { path: previousPage, label: "Back", wasVisited };
      }
      if (previousPage && previousPage.includes("/payments")) {
        const wasVisited = true; // Previous page was definitely visited
        return { path: previousPage, label: "Back", wasVisited };
      }
      // If coming from an edit page, ignore it and go to students list
      if (previousPage && previousPage.includes("/edit")) {
        const wasVisited = wasActuallyVisited("/students");
        return { path: "/students", label: "Back", wasVisited };
      }
      const wasVisited = wasActuallyVisited("/students");
      return { path: "/students", label: "Back", wasVisited };
    }

    // Payment detail pages - go to payments list (unless specific context)
    if (currentPath.match(/^\/payments\/[^\/]+$/)) {
      const previousPage = stack.length > 1 ? stack[stack.length - 2] : null;

      if (previousPage && previousPage.includes("/students/")) {
        const wasVisited = true; // Previous page was definitely visited
        return { path: previousPage, label: "Back", wasVisited };
      }
      // If coming from an edit page, ignore it and go to payments list
      if (previousPage && previousPage.includes("/edit")) {
        const wasVisited = wasActuallyVisited("/payments");
        return { path: "/payments", label: "Back", wasVisited };
      }
      const wasVisited = wasActuallyVisited("/payments");
      return { path: "/payments", label: "Back", wasVisited };
    }

    // Course detail pages
    if (currentPath.match(/^\/courses\/[^\/]+$/)) {
      const wasVisited = wasActuallyVisited("/courses");
      return { path: "/courses", label: "Back", wasVisited };
    }

    // Edit pages - always go to the detail view (use "Go to" regardless of visit status)
    if (currentPath.includes("/edit")) {
      const detailPath = currentPath.replace("/edit", "");
      return { path: detailPath, label: "Go to", wasVisited: false };
    }

    // Add pages - use hierarchical navigation (use "Go to" regardless of visit status)
    if (currentPath.includes("/add") || currentPath.includes("/new")) {
      // For add lab pages, go to the series
      if (
        currentPath.includes("/series/") &&
        currentPath.includes("/labs/add")
      ) {
        const seriesId = currentPath.match(/\/series\/([^\/]+)/)?.[1];
        if (seriesId) {
          const seriesPath = `/series/${seriesId}`;
          return { path: seriesPath, label: "Go to", wasVisited: false };
        }
      }
      // For add run pages, go to the series
      if (
        currentPath.includes("/series/") &&
        currentPath.includes("/runs/add")
      ) {
        const seriesId = currentPath.match(/\/series\/([^\/]+)/)?.[1];
        if (seriesId) {
          const seriesPath = `/series/${seriesId}`;
          return { path: seriesPath, label: "Go to", wasVisited: false };
        }
      }

      // For general add pages, use fallback or try to derive parent
      if (currentPath === "/series/add") {
        return { path: "/series", label: "Go to", wasVisited: false };
      }
      if (currentPath === "/students/add") {
        return { path: "/students", label: "Go to", wasVisited: false };
      }
      if (currentPath === "/payments/add" || currentPath === "/payments/new") {
        return { path: "/payments", label: "Go to", wasVisited: false };
      }
      if (currentPath === "/courses/add") {
        return { path: "/courses", label: "Go to", wasVisited: false };
      }

      // Use history as last resort for add pages
      const previousPage = stack.length > 1 ? stack[stack.length - 2] : null;

      if (
        previousPage &&
        !previousPage.includes("/add") &&
        !previousPage.includes("/new")
      ) {
        return { path: previousPage, label: "Go to", wasVisited: false };
      }
    }

    // For all other cases, try browser history first, then fallback
    if (stack.length > 1) {
      const previousPage = stack[stack.length - 2];
      if (previousPage && previousPage !== currentPath) {
        const wasVisited = true; // Previous page was definitely visited
        return { path: previousPage, label: "Back", wasVisited };
      }
    }

    // Fallback to provided path
    const wasVisited = wasActuallyVisited(fallbackPath);
    return { path: fallbackPath, label: "Back", wasVisited };
  }, [location.pathname, fallbackPath]);

  const backInfo = getBackDestination();

  // Generate enhanced label when destination changes
  useEffect(() => {
    const updateEnhancedLabel = async () => {
      try {
        debugLog("Generating enhanced label for:", {
          path: backInfo.path,
          wasVisited: backInfo.wasVisited,
          currentPath: location.pathname,
        });
        const enhanced = await generateEnhancedLabel(
          backInfo.path,
          backInfo.wasVisited
        );
        debugLog("Enhanced label generated:", enhanced);
        setEnhancedLabel(enhanced);
      } catch (error) {
        debugLog("Error generating enhanced label:", error);
        setEnhancedLabel(backInfo.label); // Fallback to basic label
      }
    };

    updateEnhancedLabel();
  }, [backInfo.path, backInfo.wasVisited, backInfo.label]);

  const smartBack = useCallback(() => {
    navigate(backInfo.path);
  }, [navigate, backInfo.path]);

  return {
    smartBack,
    backDestination: backInfo.path,
    backLabel: enhancedLabel, // Use enhanced label instead of basic label
  };
};
